import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { cn, downloadURI } from 'utils/helpers';
import { IoEye } from 'react-icons/io5';
import { IoMdDownload } from 'react-icons/io';
import { DeleteItemModal, Tooltip } from 'ui/components/common';
import { Attachment } from 'utils/types';
import { TrashIcon } from 'ui/components/icon';
import { SIZE } from 'utils/constants';
import { ModalWrapper } from 'ui/partials/Modal/Modal';
import useModal from 'ui/partials/Modal/useModal';

import { useDeleteAttachment, useGetAttachments } from '../hooks';
import AttachedListItem from './AttachedListItem';
import Preview from './Preview';

type PROPS = {
  referenceId: string;
  isComment?: boolean;
  noteId?: string;
  orderId?: string;
  source: 'Comment' | 'Lead' | 'Company' | 'Task' | 'OrderInvoice' | 'Deal';
  onPreviewClose: () => void;
};

const ViewAllAttachmentsModal: FC<PROPS> = ({
  referenceId,
  source,
  isComment,
  noteId,
  orderId,
}) => {
  const { data } = useGetAttachments({ referenceId, source });
  const attachments = data?.data;
  const [openPreview, setOpenPreview] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<number>(0);
  const [showDelete, setShowDelete] = useState(false);
  const { hideModal } = useModal();
  const { mutate: deleteAttachment } = useDeleteAttachment({
    onCancel: () => setShowDelete(false),
    referenceId,
  });

  const [fileToDelete, setFileToDelete] = useState('');

  const showPreview = (index: number) => {
    setOpenPreview(true);
    setFileToPreview(index);
  };

  const closePreview = () => setOpenPreview(false);

  const handleDelete = useCallback(() => {
    deleteAttachment({
      id: fileToDelete,
      referenceId,
      isComment,
      noteId,
      orderId,
    });
  }, [deleteAttachment, fileToDelete, isComment, noteId, orderId, referenceId]);

  const handleCancel = () => {
    setShowDelete(false);
    setFileToDelete('');
  };

  useEffect(() => {
    if (data?.meta.total === 0) {
      hideModal();
    }
  }, [data?.meta.total, hideModal]);

  const deleteModal = useMemo(() => {
    return (
      <ModalWrapper
        title="Attachment"
        isOpen={showDelete}
        closeModal={() => setShowDelete(false)}
        content={
          <DeleteItemModal
            onCancel={handleCancel}
            onClickDelete={handleDelete}
            text="Attachment"
            validation
          />
        }
      />
    );
  }, [handleDelete, showDelete]);

  const prepareDelete = (id: string) => {
    setFileToDelete(id);
    setShowDelete(true);
  };

  return (
    <>
      {deleteModal}
      <Preview
        open={openPreview}
        closeModal={closePreview}
        initialSlide={fileToPreview}
        files={attachments as Attachment[]}
      />
      <div className="space-y-3 max-h-[800px] overflow-y-auto scrollbar-thin">
        {attachments?.map((attachment, index) => (
          <div
            className={cn(
              'p-3',
              'bg-fill-primary',
              'border-[0.5px]',
              'border-separator-secondary',
              'flex justify-between gap-4',
            )}
          >
            <div className="flex items-center gap-4">
              <AttachedListItem size={SIZE.Medium} file={attachment} />
              <div className="text-sm break-all">{attachment.fileName}</div>
            </div>
            <div className="flex items-center gap-4">
              <Tooltip title={'Remove File'}>
                <button
                  onClick={() => prepareDelete(attachment._id as string)}
                  className={cn(
                    'rounded-full',
                    'grid',
                    'place-items-center',
                    'w-9',
                    'h-9',
                    'bg-secondary-lightest',
                  )}
                >
                  <TrashIcon className="w-5 h-5 " />
                </button>
              </Tooltip>
              <Tooltip title={'Preview File'}>
                <button
                  onClick={() => showPreview(index)}
                  className={cn(
                    'disabled:opacity-50',
                    'rounded-full',
                    'grid',
                    'place-items-center',
                    'w-9',
                    'h-9',
                    'bg-secondary-lightest',
                  )}
                >
                  <IoEye />
                </button>
              </Tooltip>
              <Tooltip title="Download File">
                <button
                  onClick={() =>
                    downloadURI(attachment.fileUrl, attachment.fileName)
                  }
                  className="rounded-full grid place-items-center w-9 h-9 bg-info-base"
                >
                  <IoMdDownload className="text-fill-tertiary" />
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ViewAllAttachmentsModal;
